import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "goober";
import type { ButtonProps } from "clutch/src/Button/Button.jsx";
import { Button } from "clutch/src/Button/Button.jsx";

import DownloadIcon from "@/inline-assets/download.svg";
import Mac from "@/inline-assets/mac.svg";
import Windows from "@/inline-assets/windows.svg";
import Ripple from "@/shared/Ripple.jsx";
import { classNames } from "@/util/class-names.mjs";
import downloadApp, { useDownloadLink } from "@/util/download-app.mjs";
import getOSType from "@/util/get-os-type.mjs";

const DownloadCSS = () => css`
  --border: none !important;
  color: white !important;
  background-image: linear-gradient(
    130deg,
    hsla(353, 85%, 53%, 1),
    hsla(346, 73%, 49%, 1)
  ) !important;

  &:hover {
    --border: none !important;
    color: white !important;
    filter: saturate(1.5) brightness(1.05) !important;
  }
`;

type DownloadButtonWrapperProps = Omit<ButtonProps<"a">, "href"> & {
  noIcon?: boolean;
  buttonOsType?: string;
  uiLocation?: string;
  noClassOverride?: boolean;
};

export const DownloadOsIcon = (props: { osType?: string }) => {
  const [osType, setOsType] = useState(props.osType ?? null);

  useEffect(() => {
    if (props.osType) setOsType(props.osType);
    else setOsType(getOSType());
  }, [props.osType]);

  return osType === "darwin" ? <Mac /> : <Windows />;
};

const DownloadButtonWrapper = (
  {
    noIcon,
    buttonOsType,
    className,
    style,
    uiLocation,
    children,
    noClassOverride,
    ...props
  }: DownloadButtonWrapperProps,
  ref,
) => {
  const [osType, setOsType] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (buttonOsType) setOsType(buttonOsType);
    else setOsType(getOSType());
  }, [buttonOsType]);

  const downloadUrl = useDownloadLink();
  const internalRef = useRef();

  return (
    <Button
      ref={ref ?? internalRef}
      /*{...props}*/
      {...classNames(!noClassOverride && DownloadCSS(), className)}
      data-tooltip={osType === "darwin" ? null : "Windows 64-bit"}
      data-place={"left"}
      type="link"
      href={downloadUrl}
      iconLeft={!noIcon && children ? <DownloadOsIcon /> : <DownloadIcon />}
      onClick={(e) => {
        if (!props.target) return;
        downloadApp(e, uiLocation);
      }}
      After={<Ripple />}
      style={style}
    >
      {children ??
        t("common:downloadBlitzForOS", "Download for {{os}}", {
          os: osType === "darwin" ? "macOS" : "Windows",
        })}
    </Button>
  );
};

const DownloadButtonWrapperWithRef = forwardRef(DownloadButtonWrapper);

export const BodyDownloadButton = ({ ...props }) => {
  const ref = useRef();

  return <DownloadButtonWrapperWithRef {...props} ref={ref} />;
};

export default DownloadButtonWrapperWithRef;
